import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { IndianRupee, TrendingUp, Users, CreditCard } from 'lucide-react';

const PlatformRevenue = () => {
  const { apiFetch } = usePlatformAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiFetch('/revenue');
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatINR = (n) => '₹' + (n || 0).toLocaleString('en-IN');
  const planLabels = { trial: 'Trial (Free)', starter: 'Starter (₹1,999)', professional: 'Professional (₹4,999)', enterprise: 'Enterprise (₹14,999)' };
  const planColors = { trial: 'border-slate-600', starter: 'border-blue-600', professional: 'border-indigo-600', enterprise: 'border-amber-600' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="platform-revenue">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Revenue & Subscriptions</h1>
        <p className="text-slate-400">Financial overview and subscription metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-indigo-600/20 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-indigo-400" /></div>
              <p className="text-sm text-slate-400">Monthly Revenue (MRR)</p>
            </div>
            <p className="text-3xl font-bold text-white">{formatINR(data?.total_mrr)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-emerald-600/20 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-emerald-400" /></div>
              <p className="text-sm text-slate-400">Annual Run Rate (ARR)</p>
            </div>
            <p className="text-3xl font-bold text-white">{formatINR(data?.total_arr)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-amber-600/20 flex items-center justify-center"><CreditCard className="h-5 w-5 text-amber-400" /></div>
              <p className="text-sm text-slate-400">Paying Schools</p>
            </div>
            <p className="text-3xl font-bold text-white">{data?.total_paying || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-cyan-600/20 flex items-center justify-center"><Users className="h-5 w-5 text-cyan-400" /></div>
              <p className="text-sm text-slate-400">Free Trial Schools</p>
            </div>
            <p className="text-3xl font-bold text-white">{data?.total_trial || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white">Revenue by Plan</CardTitle></CardHeader>
        <CardContent>
          {data?.revenue_by_plan && Object.keys(data.revenue_by_plan).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(data.revenue_by_plan).map(([plan, info]) => (
                <div key={plan} className={`flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border-l-4 ${planColors[plan] || 'border-slate-600'}`}>
                  <div>
                    <p className="text-white font-semibold capitalize">{planLabels[plan] || plan}</p>
                    <p className="text-sm text-slate-400">{info.count} school(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-white">{formatINR(info.revenue)}</p>
                    <p className="text-xs text-slate-500">per month</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">No revenue data yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformRevenue;
